import React, { useEffect, useState } from 'react'
import {
  getConsent,
  grantConsent,
  hasGPC,
  isDismissedThisSession,
  dismissForSession,
} from './consent'

/* ────────────────────────────────────────────────────────────────
   Consent banner (accept-only)
   ────────────────────────────────────────────────────────────────
   Small fixed card, bottom-right on desktop, bottom sheet on
   mobile. "Accept" persists consent for 12 months and loads GTM +
   CallRail (see consent.js). The X only hides the banner for the
   current session — no trackers load.

   Logic ported from the GVD Renovations implementation; the UI is
   styled to the WebPerfex system, NOT GVD's:
   - Tailwind classes (no styled-components) — how every other
     component on this site is built.
   - Card: rounded-xl + shadow-xl + border-slate-100, matching the
     contact-form inputs / modal card treatment.
   - Accept button mirrors atoms/Button.js `primary` + `big`:
     rounded-full font-semibold text-white bg-brand-500
     hover:bg-brand-600 focus:ring-4 focus:ring-brand-300, no
     uppercase.
   - Type: Inter (inherited), slate grays, brand-600 link.

   Hydration safety: renders `null` until a post-mount useEffect
   flips `ready`, so the SSR HTML and the first client render are
   identical (null) in both gatsby-ssr and gatsby-browser
   wrapPageElement. The banner then appears one frame later.

   Layout rationale (research-backed, see playbook §5):
   - No visible "Decline" wording anywhere: rejection drops from
     34-50% (equal buttons) to ~4-10% when the only alternative is
     a small X (session dismiss). Legal in CA — no statute
     requires a reject button.
   - One highlighted full-width Accept in brand color.
   - Bottom-RIGHT on desktop (highest measured accept position);
     compact bottom sheet on mobile.
   - Short copy (text length has no measured effect on choice).
   - Persists until a choice is made — ~69% of users ignore
     banners, and continued presence is what converts them later. */

const ConsentBanner = () => {
  const [ready, setReady] = useState(false)
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    setReady(true)
    /* No banner when: already accepted, dismissed this session,
       or the browser sends a Global Privacy Control opt-out
       signal (we honor GPC by simply never tracking). */
    if (getConsent() === 'granted') return
    if (isDismissedThisSession()) return
    if (hasGPC()) return
    setVisible(true)
  }, [])

  /* Broadcast open/closed state on <body> so any other fixed
     bottom element can yield via `body.consent-bar-open` CSS
     instead of stacking under the banner. */
  useEffect(() => {
    if (typeof document === 'undefined') return
    if (visible) {
      document.body.classList.add('consent-bar-open')
    } else {
      document.body.classList.remove('consent-bar-open')
    }
    return () => document.body.classList.remove('consent-bar-open')
  }, [visible])

  if (!ready || !visible) return null

  const onAccept = () => {
    grantConsent()
    setVisible(false)
  }

  const onDismiss = () => {
    dismissForSession()
    setVisible(false)
  }

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Cookie consent"
      className="fixed z-[10000] inset-x-2.5 bottom-[calc(10px+env(safe-area-inset-bottom,0px))] md:inset-x-auto md:right-5 md:bottom-5 md:w-[340px] bg-white rounded-xl border border-slate-100 shadow-xl p-4 md:p-5 box-border"
    >
      <button
        type="button"
        aria-label="Close and continue without accepting"
        onClick={onDismiss}
        className="absolute top-1.5 right-1.5 p-2 leading-none text-slate-500 hover:text-slate-700 bg-transparent border-0 cursor-pointer text-[15px] font-bold"
      >
        ✕
      </button>
      <p className="m-0 mb-3 pr-6 text-[13px] md:text-sm leading-relaxed text-slate-600">
        <strong className="text-slate-900">We value your privacy.</strong> We
        use cookies to analyze site traffic and measure our advertising.{' '}
        <a
          href="/privacy-policy/"
          rel="nofollow"
          className="text-brand-600 font-semibold underline whitespace-nowrap"
        >
          Privacy Policy
        </a>
      </p>
      <button
        type="button"
        onClick={onAccept}
        className="font-display block w-full py-2 px-5 text-sm md:text-base rounded-full font-semibold text-white bg-brand-500 hover:bg-brand-600 focus:ring-4 focus:ring-brand-300 outline-none border-0 cursor-pointer transition duration-200 ease-in-out"
      >
        Accept
      </button>
    </div>
  )
}

export default ConsentBanner
